// =============================================================================
// useUniqueSubpages — stan podstron per mapa (propozycja / edycja / generacja / publikacja).
// =============================================================================

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { proposeTopics } from "@/services/uniquePages/topicProposer";
import { generateSubpage } from "@/services/uniquePages/subpageGenerator";
import { publishSubpageToWp } from "@/services/uniquePages/publishHtmlAsWpPage";
import type { UniqueSubpage } from "@/types/uniquePages";

export function useUniqueSubpages(mapId: string | undefined) {
  const [subpages, setSubpages] = useState<UniqueSubpage[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!mapId) return;
    setLoading(true);
    try {
      const { data } = await (supabase as any)
        .from("unique_subpages")
        .select("*")
        .eq("map_id", mapId)
        .order("created_at", { ascending: true });
      setSubpages((data || []) as UniqueSubpage[]);
    } finally {
      setLoading(false);
    }
  }, [mapId]);

  useEffect(() => { void load(); }, [load]);

  const propose = useCallback(async () => {
    if (!mapId) return { status: "error" as const, reason: "brak mapId" };
    const res = await proposeTopics(mapId);
    await load();
    return res;
  }, [mapId, load]);

  const addManual = useCallback(async (title: string, topic: string) => {
    if (!mapId || !title.trim()) return;
    const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
    await (supabase as any).from("unique_subpages").insert({
      user_id: userId, map_id: mapId, title: title.trim(), topic: topic.trim() || null, kind: "custom", status: "draft_topic",
    });
    await load();
  }, [mapId, load]);

  const updateFields = useCallback(async (id: string, fields: Partial<Pick<UniqueSubpage, "title" | "topic" | "html">>) => {
    await (supabase as any).from("unique_subpages").update({ ...fields, updated_at: new Date().toISOString() }).eq("id", id);
    await load();
  }, [load]);

  const remove = useCallback(async (id: string) => {
    await (supabase as any).from("unique_subpages").delete().eq("id", id);
    await load();
  }, [load]);

  const generate = useCallback(async (id: string) => {
    setBusyId(id);
    try {
      const res = await generateSubpage(id);
      await load();
      return res;
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const publish = useCallback(async (id: string, websiteId: string) => {
    setBusyId(id);
    try {
      const res = await publishSubpageToWp(id, websiteId);
      await load();
      return res;
    } finally {
      setBusyId(null);
    }
  }, [load]);

  return { subpages, loading, busyId, refresh: load, propose, addManual, updateFields, remove, generate, publish };
}
